/**
 * The vocabulary every non-board screen is built from, measured across
 * `historic-mission-listbox.png`, `dogfight-famous-ace-roster.png`,
 * `flight-assignments.png` and `career-enlist-name.png` and found identical on
 * all four. There are only two frame families and one text rule:
 *
 * - a **raised plate** (boards, button surrounds, value fields): two rings,
 *   `[top 224, bottom 236, side 234]` then `[226, 233, 232]`, over fill 228;
 * - a **sunken well** (list wells, portraits, the text-entry field): the same
 *   two rings with top and bottom exchanged, over black or an image;
 * - text is embossed **relative to whatever it sits on**. On the tan ramp,
 *   where a higher index is darker, the stroke's top edge is `B+6`, its body
 *   `B+3` and its one-row drop shadow `B-3`; on the orange ramp, where the
 *   sense of the ramp reverses, so do the signs. `B=249` reproduces the Main
 *   Menu's own `243/246/252` label, which is the cross-check that the rule is
 *   the original's and not a curve fitted to one screen.
 *
 * Buttons are the Main Menu's five-ring stack at any width; the label centring
 * rule is `menu-chrome.ts`'s, verified again here against thirteen more
 * labels.
 */

import { bevelRings, type BevelStack, type DrawOp, fill, type FontId, type Rect, type TextStyle } from "./draw-ops.ts";
import { TAN_FACE } from "./ps-palette.ts";
import { type FontAtlas, measureText } from "./text-metrics.ts";

export const RAISED_RINGS: BevelStack = [
  [224, 236, 234],
  [226, 233, 232],
];

export const SUNKEN_RINGS: BevelStack = [
  [233, 224, 232],
  [236, 226, 234],
];

/**
 * A well sunk into the **orange** plate rather than the tan one — the eight
 * value wells on MISSION DEBRIEFING. Measured off
 * `mission-debriefing.png`'s AIRCRAFT/PERSONAL well, which frames its 21×11
 * box `246` outside and `244` in on three sides, `252` and `250` beneath.
 * Drawing tan rings there was a real departure: this lane found it by sampling
 * the rendered screen against the capture, which is also how it is now checked.
 */
export const ORANGE_SUNKEN_RINGS: BevelStack = [
  [246, 252, 246],
  [244, 250, 244],
];

/** The plate face every raised frame is filled with, and the reference background for embossed text. */
export const PLATE_FILL = TAN_FACE;
/** The flat tan a full-bleed panel's body is painted in — one index darker than a plate face. */
export const PANEL_FILL = 229;
export const WELL_FILL = 0;

/** Emboss triple for text drawn on `background`. `orange` flips the sense, because the burnt-orange ramp runs the other way. */
export function embossOn(background: number, orange = false): TextStyle {
  const sign = orange ? -1 : 1;
  return { body: background + 3 * sign, shadow: background + 6 * sign, highlight: background - 3 * sign };
}

/** Inside a boxed template slot, which is painted one index lighter than the plate. */
export const SLOT_FILL = 227;

export function raisedPlateOps(rect: Rect, face = PLATE_FILL): DrawOp[] {
  return [fill({ x: rect.x + 2, y: rect.y + 2, w: rect.w - 4, h: rect.h - 4 }, face), ...bevelRings(rect, RAISED_RINGS)];
}

export function sunkenWellOps(rect: Rect, face: number = WELL_FILL, rings: BevelStack = SUNKEN_RINGS): DrawOp[] {
  return [fill({ x: rect.x + 2, y: rect.y + 2, w: rect.w - 4, h: rect.h - 4 }, face), ...bevelRings(rect, rings)];
}

/**
 * The burnt-orange plate — the MISSION DEBRIEFING score face, and the only
 * frame in the shell drawn on the orange ramp. Two rings like a tan plate, with
 * the ramp's sense reversed so the lit edge is still the top.
 */
export const ORANGE_RINGS: BevelStack = [
  [254, 245, 245],
  [251, 247, 247],
];

/** The orange face itself, one index either side of the ramp's middle. */
export const ORANGE_FACE = 249;

/**
 * The four corners are **cut**, and the cut is measured rather than derived.
 * `docs/game/ui-reference/mission-debriefing.png` takes three pixels off each
 * corner in a 45° staircase and shades the exposed facet — brighter than the
 * top ring at the top of the plate, darker than the side rings at the bottom.
 * Two rows of the top cut carry that facet a row further than the geometry
 * would, which is why this is a stamp and not a formula.
 *
 * Each entry is `[dx, dy, index]` from the plate's top-left. `PLATE_FILL` means
 * *cut away* — the tan the orange plate sits on shows through, which is what
 * the capture holds there.
 */
const CHAMFER_TOP: readonly (readonly [number, number, number])[] = [
  [0, 0, PLATE_FILL], [1, 0, PLATE_FILL], [2, 0, PLATE_FILL],
  [3, 0, 253], [4, 0, 253], [5, 0, 253], [6, 0, 253], [7, 0, 253],
  [0, 1, PLATE_FILL], [1, 1, PLATE_FILL], [2, 1, 253],
  [0, 2, PLATE_FILL], [1, 2, 253], [2, 2, 251],
  [0, 3, 253], [1, 3, 251],
];

/** Same, from the plate's bottom-left; `dy` counts up from the last row. */
const CHAMFER_BOTTOM: readonly (readonly [number, number, number])[] = [
  [0, 0, PLATE_FILL], [1, 0, PLATE_FILL], [2, 0, PLATE_FILL],
  [0, 1, PLATE_FILL], [1, 1, PLATE_FILL], [2, 1, 245],
  [0, 2, PLATE_FILL], [1, 2, 245], [2, 2, 247],
];

/**
 * A rivet head, 5×4, as the capture draws it: a lit top edge, a dark shank and
 * a bright right shoulder. Rows run top to bottom; the right-hand pair of
 * rivets are this mirrored, which is what the capture shows and is why the
 * light lands on the outside of the plate on both sides.
 */
const RIVET: readonly (readonly number[])[] = [
  [251, 253, 253, 253, 251],
  [245, 250, 250, 250, 253],
  [245, 247, 247, 250, 253],
  [248, 245, 245, 245, 250],
];

/** Measured inset of a rivet's own box from the plate's corner. */
const RIVET_INSET = { x: 3, top: 4, bottom: 9 } as const;

function rivetOps(x: number, y: number, mirrored: boolean): DrawOp[] {
  return RIVET.flatMap((row, dy) =>
    row.map((index, dx) => fill({ x: x + (mirrored ? row.length - 1 - dx : dx), y: y + dy, w: 1, h: 1 }, index)),
  );
}

/**
 * The debriefing's score plate: `ORANGE_RINGS` over `ORANGE_FACE`, with the
 * corners cut and the four rivets set in. Split out beside `raisedPlateOps`
 * rather than made a new `Widget` kind because this is one screen's frame, not
 * a new vocabulary (G55).
 */
export function chamferedPlateOps(rect: Rect): DrawOp[] {
  const right = rect.x + rect.w - 1;
  const bottom = rect.y + rect.h - 1;
  const corners = [
    ...CHAMFER_TOP.flatMap(([dx, dy, index]) => [
      fill({ x: rect.x + dx, y: rect.y + dy, w: 1, h: 1 }, index),
      fill({ x: right - dx, y: rect.y + dy, w: 1, h: 1 }, index),
    ]),
    ...CHAMFER_BOTTOM.flatMap(([dx, dy, index]) => [
      fill({ x: rect.x + dx, y: bottom - dy, w: 1, h: 1 }, index),
      fill({ x: right - dx, y: bottom - dy, w: 1, h: 1 }, index),
    ]),
  ];
  return [
    ...bevelRings(rect, ORANGE_RINGS),
    fill({ x: rect.x + 2, y: rect.y + 2, w: rect.w - 4, h: rect.h - 4 }, ORANGE_FACE),
    ...corners,
    ...rivetOps(rect.x + RIVET_INSET.x, rect.y + RIVET_INSET.top, false),
    ...rivetOps(right - RIVET_INSET.x - (RIVET[0]?.length ?? 0) + 1, rect.y + RIVET_INSET.top, true),
    ...rivetOps(rect.x + RIVET_INSET.x, bottom - RIVET_INSET.bottom + 1, false),
    ...rivetOps(right - RIVET_INSET.x - (RIVET[0]?.length ?? 0) + 1, bottom - RIVET_INSET.bottom + 1, true),
  ];
}

/**
 * A boxed template slot — the original's `[in the middle of the war]`. One
 * ring, top light and bottom dark like a plate but without the second, over a
 * face one index lighter than the plate it sits on. Text is inset 3px and the
 * box starts two rows above the glyph tops.
 */
export const SLOT_TEXT_INSET = 3;
export const SLOT_HEIGHT = 9;

export function slotRect(x: number, y: number, w: number): Rect {
  return { x, y: y - 2, w, h: SLOT_HEIGHT };
}

/**
 * `focused` nudges the fill one index brighter — the same "+1 on focus" idiom
 * `widget-ops.ts`'s `chipOps` uses for a value chip's own ring — so a slot the
 * port has made clickable (AD-011) shows a cursor can rest on it without a new
 * capture to measure a highlight against.
 */
export function slotOps(rect: Rect, focused = false): DrawOp[] {
  return [
    fill({ x: rect.x + 1, y: rect.y + 1, w: rect.w - 2, h: rect.h - 2 }, focused ? SLOT_FILL + 1 : SLOT_FILL),
    ...bevelRings(rect, [[224, 236, 233]]),
  ];
}

/**
 * The width a slot needs: measured across all seven of `patrol-briefing.png`'s
 * boxes as `inkWidth + 5`, i.e. the advance plus four — three pixels of inset
 * on the left, the two borders, and the one unfilled column the original
 * leaves before the right border.
 */
export function slotWidth(font: FontAtlas, text: string): number {
  return measureText(font, text) + 4;
}

export const LIST_ROW_PITCH = 6;
export const LIST_ROW_TEXT_INSET_X = 8;
export const LIST_TEXT: number = 236;
export const LIST_SELECTED_TEXT = 248;
export const LIST_SELECTED_BORDER = 239;
export const LIST_SELECTED_FILL = 223;
export const LIST_FONT: FontId = "FONT3";

/** The selection bar: a 7-row box spanning the well, `239` outlined and `223` filled, with the row's text switched to `248`. */
export function listSelectionOps(well: Rect, row: number): DrawOp[] {
  const rect = { x: well.x, y: well.y + row * LIST_ROW_PITCH, w: well.w, h: 7 };
  return [...bevelRings(rect, [[LIST_SELECTED_BORDER, LIST_SELECTED_BORDER, LIST_SELECTED_BORDER]]), fill(
    { x: rect.x + 1, y: rect.y + 1, w: rect.w - 2, h: rect.h - 2 },
    LIST_SELECTED_FILL,
  )];
}

/**
 * The up/down scroll buttons: a 17×17 orange button with the arrow drawn from
 * the measured stroke rows. `ESNTL.BMP` has no arrow frame and the glyph is in
 * no baked font, so — like the board's bevels — the screenshot is the source.
 */
export const ARROW_SIZE = 17;
const ARROW_DARK = 243;
const ARROW_BODY = 246;
const ARROW_LIGHT = 252;

/** `[row, [x…] dark, [x…] body, [x…] light]`, relative to the button's top-left. */
const UP_ARROW: readonly (readonly [number, readonly number[], readonly number[], readonly number[]])[] = [
  [3, [8, 9], [], []],
  [4, [7, 10], [8, 9], []],
  [5, [6, 11], [7, 8, 9, 10], []],
  [6, [5, 12], [6, 7, 8, 9, 10, 11], []],
  [7, [4, 13], [5, 6, 7, 8, 9, 10, 11, 12], []],
  [8, [], [7, 8, 9, 10], [4, 5, 6, 11, 12, 13]],
  [9, [], [7, 8, 9, 10], []],
  [10, [], [7, 8, 9, 10], []],
  [11, [], [7, 8, 9, 10], []],
  [12, [], [7, 8, 9, 10], []],
  [13, [], [], [7, 8, 9, 10]],
];

const DOWN_ARROW: readonly (readonly [number, readonly number[], readonly number[], readonly number[]])[] = [
  [3, [7, 8, 9, 10], [], []],
  [4, [], [7, 8, 9, 10], []],
  [5, [], [7, 8, 9, 10], []],
  [6, [], [7, 8, 9, 10], []],
  [7, [], [7, 8, 9, 10], []],
  [8, [4, 5, 6, 11, 12, 13], [7, 8, 9, 10], []],
  [9, [], [5, 6, 7, 8, 9, 10, 11, 12], [4, 13]],
  [10, [], [6, 7, 8, 9, 10, 11], [5, 12]],
  [11, [], [7, 8, 9, 10], [6, 11]],
  [12, [], [8, 9], [7, 10]],
  [13, [], [], [8, 9]],
];

export function arrowOps(rect: Rect, direction: "up" | "down"): DrawOp[] {
  const rows = direction === "up" ? UP_ARROW : DOWN_ARROW;
  const ops: DrawOp[] = [];
  for (const [row, dark, body, light] of rows) {
    for (const [xs, color] of [
      [dark, ARROW_DARK],
      [body, ARROW_BODY],
      [light, ARROW_LIGHT],
    ] as const) {
      for (const x of xs) ops.push(fill({ x: rect.x + x, y: rect.y + row, w: 1, h: 1 }, color));
    }
  }
  return ops;
}

/**
 * The stacked rule-pairs that flank a heading or a button row: four bars, each
 * two rows tall — dark `235` over light `226` — shortening by five pixels per
 * step away from the text.
 */
export function ruleFanOps(x: number, y: number, length: number, toward: -1 | 1): DrawOp[] {
  const ops: DrawOp[] = [];
  for (let i = 0; i < 4; i += 1) {
    const width = length - i * 5;
    const left = toward < 0 ? x : x + i * 5;
    ops.push(fill({ x: left, y: y + i * 2, w: width, h: 1 }, 235), fill({ x: left, y: y + i * 2 + 1, w: width, h: 1 }, 226));
  }
  return ops;
}

/** Left edge for a label centred in a plate, by the rule that reproduces every measured button on every capture. */
export function plateTextX(font: FontAtlas, text: string, plate: Rect): number {
  return plate.x + Math.floor((plate.w - (measureText(font, text) - 1) + 1) / 2);
}
